import { startOfTomorrow } from 'date-fns/startOfTomorrow';
import React, { useEffect, useMemo, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { Label } from '@eika/label';

import { useConfig } from '../../../../defaultConfig';
import { useFirstBankingDayDispatch } from '../../../../fetchHooks/useBankingDays';
import usePaymentLimits from '../../../../fetchHooks/usePaymentLimits';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useCurrency from '../../../../hooks/useCurrency';
import useHandleCrossBorderReload from '../../../../hooks/useHandleCrossBorderReload';
import useTranslation from '../../../../hooks/useTranslation';
import { AmountInput, DateSelect } from '../../../NettbankCreateTransactions/components';
import { NavigationButtons } from '../../../shared/NavigationButtons';
import ChargeBearerSelect from '../ChargeBearerSelect';
import CurrencyExchange from '../CurrencyExchange';
import MessageReceiverField from '../MessageReceiverField';
import PrioritySelect from '../PrioritySelect';
import BicInput from '../Receiver/BicInput';
import { CrossBorderPaymentActionType } from '../form/actions';
import { GB_COUNTRY_CODE, GB_CURRENCY } from '../form/state';
import useFormContext from '../form/useFormContext';
import {
    AmountFieldsError,
    isDomesticPayment,
    isNoCoverage,
    validateAmountFields,
    validateAmount,
} from '../form/validate';
import CurrencySelect from './CurrencySelect';
import TransferCurrencySelect from './TransferCurrencySelect';

import './NettbankAmountFields.scss';

const today = getZonedDate();
const tomorrow = getZonedDate(startOfTomorrow());

const NettbankAmountFields: React.FC = () => {
    const translated = useTranslation();
    const { state, handleChange, errors } = useFormContext();
    const [validationErrors, setValidationErrors] = useState<AmountFieldsError>();
    const [calculatedCurrencyExchange, setCalculatedCurrencyExchange] = useState<number>();
    const defaultCurrency = useCurrency(state);
    const notDefaultCountryCurrency = defaultCurrency !== state.country?.currency && state.receiver?.needBic;
    useHandleCrossBorderReload();
    const { featureToggles } = useConfig();

    const { targetFromAccount } = usePaymentAccountContext();

    useFirstBankingDayDispatch(tomorrow, handleChange, CrossBorderPaymentActionType.FIRST_BANKING_DATE_FROM_TODAY);

    const paymentLimits = usePaymentLimits();

    const needsTransferCurrency = useMemo(() => {
        const currencyIsInTransferCurrency = Boolean(
            state.country?.transferCurrencies?.some(
                (currencyItem) => currencyItem.currency.toString() === state.currency?.toString(),
            ),
        );
        return !currencyIsInTransferCurrency;
    }, [state.currency, state.country?.transferCurrencies]);

    useEffect(() => {
        handleChange({
            type: CrossBorderPaymentActionType.SET_AMOUNT_IN_NOK,
            payload: calculatedCurrencyExchange,
        });
    }, [calculatedCurrencyExchange, handleChange]);

    useEffect(() => {
        if (defaultCurrency) {
            handleChange({
                type: CrossBorderPaymentActionType.CURRENCY,
                payload: defaultCurrency,
            });
        }
    }, [defaultCurrency, handleChange]);

    const showChargeBearer = state.country?.countryCode?.value === GB_COUNTRY_CODE && state.currency === GB_CURRENCY;

    const validateAmountFieldsDesktop = (transferCurrency: boolean) => {
        const validationErrors = validateAmountFields(
            translated,
            state,
            transferCurrency,
            paymentLimits.data,
            targetFromAccount,
        );
        setValidationErrors(validationErrors);

        return !(
            validationErrors.amount ||
            validationErrors.amountLimit ||
            validationErrors.currency ||
            validationErrors.transferCurrency ||
            validationErrors.messageReceiver ||
            validationErrors.bic
        );
    };

    return (
        <>
            <h2 className="nettbank-amount-fields__title">{translated.crossBorderPayment.desktopAmountTitle}</h2>
            <AmountInput
                className="nettbank-amount-fields__amount"
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                limitWarning={validateAmount(translated, state, paymentLimits.data, targetFromAccount)?.amountLimit}
                domesticPaymentWarning={isDomesticPayment(state)}
                error={validationErrors?.amount ?? errors.amount}
                limitError={validationErrors?.amountLimit ?? errors.amountLimit}
                value={state.amount}
                currency={state.currency ?? state.country?.currency ?? 'EUR'}
                onChange={(value) => handleChange({ type: CrossBorderPaymentActionType.AMOUNT, payload: value })}
                elementToFocusOnClose={null}
                label={translated.payAmount}
                locked={false}
            />
            <div className="nettbank-amount-fields__currency-exchange">
                <CurrencyExchange
                    amount={state.amount ?? 0}
                    selectedCurrency={state.currency ?? state.country?.currency ?? 'EUR'}
                    calculatedCurrencyExchange={(calculatedAmount) => setCalculatedCurrencyExchange(calculatedAmount)}
                />
            </div>
            <CurrencySelect showKinaInfo={state.currency === 'CNY'} />
            {needsTransferCurrency && (
                <TransferCurrencySelect error={validationErrors?.transferCurrency} setError={setValidationErrors} />
            )}
            {notDefaultCountryCurrency && (
                <>
                    <BicInput
                        className="nettbank-bic-input"
                        validationError={validationErrors}
                        setValidationError={setValidationErrors}
                        enableValidation
                    />
                    <Label
                        className="nettbank-bic-input__info"
                        text={translated.crossBorderPayment.requiredBicMessage}
                    />
                </>
            )}
            <DateSelect
                className="nettbank-amount-fields__date-select"
                date={state.paymentDate ?? today}
                onChange={(date) => handleChange({ type: CrossBorderPaymentActionType.PAYMENT_DATE, payload: date })}
                error={errors.paymentDate}
                onlyBusinessDays
            />
            {(!state.country?.partOfSepa || showChargeBearer) && <ChargeBearerSelect />}
            {featureToggles.enableCrossBorderPriority && <PrioritySelect />}
            <MessageReceiverField
                resetError={() => setValidationErrors({ messageReceiver: undefined })}
                error={validationErrors?.messageReceiver}
            />
            <NavigationButtons
                centerOnSmallerWidth
                onClick={() => validateAmountFieldsDesktop(needsTransferCurrency)}
                nextUrl="kategorier"
            />
        </>
    );
};

export default NettbankAmountFields;
